import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import { crmAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const STAGES = ['new', 'contacted', 'interested', 'qualified', 'proposal_sent', 'booked', 'lost', 'unsubscribed'];
const SOURCES = ['website_form', 'inquiry', 'newsletter', 'quote_request', 'social_media', 'referral', 'walk_in', 'phone', 'chat', 'abandoned_booking'];
const STATUSES = ['Hot', 'Warm', 'Cold'];

const LeadFormPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    stage: 'new',
    source: 'website_form',
    budget: { min: '', max: '' },
    travelDates: { checkIn: '', checkOut: '' },
    guestCount: '',
    interestedProperties: [],
    notes: '',
    assignedTo: user?._id || ''
  });

  const set = (path, value) => {
    setForm(prev => {
      const parts = path.split('.');
      if (parts.length === 1) return { ...prev, [path]: value };
      if (parts.length === 2) return { ...prev, [parts[0]]: { ...prev[parts[0]], [parts[1]]: value } };
      if (parts.length === 3) return { 
        ...prev, 
        [parts[0]]: { 
          ...prev[parts[0]], 
          [parts[1]]: { 
            ...prev[parts[0]][parts[1]], 
            [parts[2]]: value 
          } 
        } 
      };
      return prev;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Clean up empty values
      const cleanForm = {
        ...form,
        budget: {
          min: form.budget.min || undefined,
          max: form.budget.max || undefined
        },
        travelDates: {
          checkIn: form.travelDates.checkIn || undefined,
          checkOut: form.travelDates.checkOut || undefined
        },
        guestCount: form.guestCount || undefined
      };

      const res = await crmAPI.createLead(cleanForm);
      toast.success('Lead created successfully');
      navigate(`/crm/leads/${res.data.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/crm')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">Add New Lead</h1>
            <p className="page-subtitle">Enter lead information to add to your pipeline</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <><div className="spinner" style={{ borderTopColor: '#fff' }} /> Saving...</> : <><Save size={16} /> Create Lead</>}
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          {/* Main Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Contact Information */}
            <div className="card">
              <div className="card-header"><span className="card-title">Contact Information</span></div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">First Name *</label>
                  <input className="form-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name *</label>
                  <input className="form-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={form.email} onChange={e => set('email', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input type="tel" className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Company</label>
                  <input className="form-input" value={form.company} onChange={e => set('company', e.target.value)} placeholder="Optional" />
                </div>
              </div>
            </div>

            {/* Lead Details */}
            <div className="card">
              <div className="card-header"><span className="card-title">Lead Details</span></div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Stage</label>
                  <select className="form-select" value={form.stage} onChange={e => set('stage', e.target.value)}>
                    {STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Source</label>
                  <select className="form-select" value={form.source} onChange={e => set('source', e.target.value)}>
                    {SOURCES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select className="form-select" value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
                    <option value={user?._id}>Me ({user?.firstName} {user?.lastName})</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Travel Information */}
            <div className="card">
              <div className="card-header"><span className="card-title">Travel Information</span></div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Check-in Date</label>
                  <input type="date" className="form-input" value={form.travelDates.checkIn} onChange={e => set('travelDates.checkIn', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Check-out Date</label>
                  <input type="date" className="form-input" value={form.travelDates.checkOut} onChange={e => set('travelDates.checkOut', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Number of Guests</label>
                  <input type="number" className="form-input" value={form.guestCount} onChange={e => set('guestCount', e.target.value)} min="1" />
                </div>
              </div>
            </div>

            {/* Budget */}
            <div className="card">
              <div className="card-header"><span className="card-title">Budget Range</span></div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Minimum Budget</label>
                  <input type="number" className="form-input" value={form.budget.min} onChange={e => set('budget.min', e.target.value)} min="0" placeholder="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Maximum Budget</label>
                  <input type="number" className="form-input" value={form.budget.max} onChange={e => set('budget.max', e.target.value)} min="0" placeholder="No limit" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card">
              <div className="card-header"><span className="card-title">Additional Notes</span></div>
              <div className="form-group">
                <textarea className="form-textarea" rows={4} placeholder="Add any additional information about this lead..."
                  value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Quick Actions</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
                  <Plus size={14} /> Add Task
                </button>
                <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
                  <Plus size={14} /> Schedule Follow-up
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><span className="card-title">Pipeline Status</span></div>
              <div style={{ fontSize: 13, color: '#6B7280' }}>
                <div>
                  <strong>Current Stage:</strong> {form.stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LeadFormPage;
